import { CJK_LOCALES, type SupportedLocale } from './supported-locales';

/**
 * Tag-level CJK check: accepts any raw BCP-47 string (or nullish), and treats a
 * bare language subtag as CJK (`ja`, `ko`, `zh`) as well as a full app locale.
 *
 * Distinct from {@link isCjkLocale} in `./supported-locales`, which is the
 * enum-level predicate — it takes an already-normalised {@link SupportedLocale}
 * and does no subtag widening. Reach for that one when you have resolved the
 * locale (e.g. via `getSupportedLocaleOrDefault`); reach for this one when you
 * are holding an unvalidated string such as a browser or OS locale.
 */
export function isCjkLocaleTag(locale: string | null | undefined): boolean {
  if (!locale) {
    return false;
  }

  if (CJK_LOCALES.has(locale as SupportedLocale)) {
    return true;
  }
  const language = locale.split('-')[0];
  return language === 'ja' || language === 'ko' || language === 'zh';
}

type NameParts = {
  firstName?: string | null;
  lastName?: string | null;
};

export function formatCjkAwareDisplayName(
  { firstName, lastName }: NameParts,
  locale: string | null | undefined
): string {
  const orderedNames = isCjkLocaleTag(locale)
    ? [lastName, firstName]
    : [firstName, lastName];

  return orderedNames
    .map((name) => name?.trim())
    .filter(Boolean)
    .join(' ');
}

export function getCjkAwareDisplayInitials(
  { firstName, lastName }: NameParts,
  locale: string | null | undefined
): string {
  const orderedNames = isCjkLocaleTag(locale)
    ? [lastName, firstName]
    : [firstName, lastName];

  return orderedNames
    .flatMap((name) => {
      const initial = [...(name?.trim() ?? '')].at(0);
      return initial ? [initial.toUpperCase()] : [];
    })
    .join('');
}

type UserNameFields = {
  first_name?: string | null;
  last_name?: string | null;
};

/** Name inserted into Evidence empty-state greetings (first name, or family name for CJK). */
export function getUserGreetingName(
  user: UserNameFields | null | undefined,
  locale: string
): string | null {
  if (!user) {
    return null;
  }

  const useFamilyName = isCjkLocaleTag(locale);
  const primary = useFamilyName ? user.last_name : user.first_name;
  const fallback = useFamilyName ? user.first_name : user.last_name;
  const name = (primary ?? fallback)?.trim();

  return name || null;
}
